import React, { useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import axiosCancel from 'axios-cancel';
import shortid from 'shortid';
import { useLocalStorage } from 'react-haiku';

import { useAppStore } from 'store/useAppStore';

axiosCancel(axios, {
	debug: false
});

const Secured = Wrapped => {
	const AuthenticationWall = props => {
		// Use local storage's token
		const [token] = useLocalStorage('token');

		// Use app's store
		const [setAuthenticated, setUser, setValid] = useAppStore(state => [state.setAuthenticated, state.setUser, state.setValid]);

		// Create unique component's id that doesn't change between renders
		const componentId = useMemo(() => shortid.generate(), []);

		// Cancel all requests on unmount
		useEffect(() => {
			return () => {
				axios.cancelAll();
			};
		}, []);

		// Create axios request
		// @method: string | http request type
		// @url: string | request's url
		// @params: object | request's parameters
		// @data: object | request's data
		// @cb: function | callback function
		const request = (method, url, params = null, data = null, cb) => {
			// Create unique request's id
			const requestId = `${componentId}.${url}`;

			// Cancel request with same id
			axios.cancel(requestId);

			// Make request
			axios({
				requestId,
				headers: {
					Authorization: token
				},
				method,
				// eslint-disable-next-line
				baseURL: process.env.REACT_APP_API,
				url,
				params,
				data
			}).then(res => {
				// Update valid authorization state
				setValid(true);

				// Return response data
				return cb(res.data);

			}).catch(err => {
				const canceled = axios.isCancel(err);
				const message = err.response?.data;
				const status = err.response?.status;

				// Do not return anything if request was cancelled
				if (canceled) return;

				// Log error if in development mode
				// eslint-disable-next-line
				if (process.env.NODE_ENV === 'development')
					console.error('evide.request.catch', url, status, message);

				// Deauthenticate user if 401 received
				if (status === 401) {
					// Update system access
					setAuthenticated(false);
					setUser({});

					// Clear local storage
					return localStorage.clear();
				}

				// Return error's data otherwise
				return cb(message);
			});
		};

		// Memoized request toggler function
		const requestMemoized = useCallback(request, []);

		return <Wrapped request={requestMemoized} {...props} />;
	};

	return AuthenticationWall;
};

export default Secured;
